// What a route shows when its loader rejects — or when its component throws.
//
// Before route loaders existed, no route could fail at load time: a failed read
// surfaced inside the screen, where `resolveBodyState` puts it in the table body
// and 56 `QueryErrorNote` call sites offer a retry. Adding loaders created a new
// failure with no owner: `warm` is a `Promise.all` over `ensureQueryData`, so one
// rejection rejects the loader, and TanStack Router fell back to its own default
// — an inline-styled "Something went wrong!" with `reset` undefined, a dead end
// recoverable only by a full page reload. Every retry affordance in the app was
// unreachable on exactly the path where a retry matters most.
//
// IT CATCHES TWO DIFFERENT THINGS, AND THEY NEED DIFFERENT WORDS.
// `Match.js:76` switches the per-match `CatchBoundary` on as soon as ANY error
// component is registered, and that boundary wraps `MatchInner` — which renders
// the route's component. So a render-time `TypeError` in a screen arrives here
// too. Calling that "couldn't load this screen" is a lie that costs debugging
// time, and offering a Retry that re-runs an already-successful loader returns
// the user to the identical crash. Verified by probe, not assumed.
//
// A 401 is deliberately not handled: `setUnauthorizedHandler` in main.tsx
// already redirects to /login, and competing with it would race a redirect
// against a rendered panel.

import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { ApiError, NetworkError } from '@/api/errors';
import { QueryErrorNote } from '@/components/error-panel';
import { requestReleaseRead } from '@/lib/app-release';

export function RouteError({ error, reset }: { error: unknown; reset?: () => void }) {
  const router = useRouter();

  // A capability the session does not have. The loader fetched something the
  // server refused — a routing mistake rather than an outage, so say that
  // instead of offering a retry that will fail identically.
  const forbidden = error instanceof ApiError && error.status === 403;
  // Did this come from a request, or from rendering?
  //
  // `ApiError` alone is not the test, and getting that wrong cost the exact
  // affordance this component exists to provide. `fetch` rejects with a bare
  // `TypeError` when the network is down, so an offline cold load — the failure
  // a retry fits best — was classified as a render crash and shown without a
  // Retry button. `client.ts` now wraps those as `NetworkError` at the throw
  // site, which is the only place that can tell them apart.
  const fromRequest = error instanceof ApiError || error instanceof NetworkError;

  // A screen that failed to render in a tab open since before a deploy may be the old bundle
  // meeting a newer API. Ask `version.json` now rather than at the next poll, so the update
  // banner can say a newer release is ready (lib/app-release.ts, 2026-10-21); a confirmed
  // update is still needed before it says anything.
  useEffect(() => {
    if (!fromRequest && __RELEASE_CHECK__) requestReleaseRead();
  }, [fromRequest]);

  const what = forbidden
    ? 'this screen — your account lacks the required permission'
    : fromRequest
      ? 'this screen'
      : 'this screen — it failed to render, so reloading is unlikely to help';

  const onRetry = forbidden || !fromRequest
    ? undefined
    : () => {
        // Both calls are needed: `reset` alone leaves the rejected loader result
        // in place, and `invalidate` alone leaves the boundary rendered.
        reset?.();
        void router.invalidate();
      };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
      <QueryErrorNote error={error} what={what} onRetry={onRetry} />
    </div>
  );
}
